// The per-file lookup index: one card per source, showing the agent-produced
// structure + citable "keys". This is the reference panel the writer scans (and
// later, deep-search agents point at) while editing the report. Sources can also
// be ADDED mid-edit: an upload is surveyed on the fly and becomes groundable.
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { MathText } from "@/components/math-text";
import { surveyReportSources, type ReportFileSurvey } from "@/api/report";
import { uploadProjectFile } from "@/api/projects";
import { Alert, Button, Spinner } from "@/components/ui";

export function LookupIndexPanel({
  surveys,
  projectId,
  onChanged,
}: {
  surveys: ReportFileSurvey[];
  projectId: string;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const fileInput = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const addSource = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      for (const f of Array.from(files)) {
        await uploadProjectFile(projectId, f, f.name, "info");
      }
      await surveyReportSources(projectId);
      onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("Could not add the source."));
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const addBar = (
    <div className="space-y-1">
      <input
        ref={fileInput}
        type="file"
        multiple
        accept=".pdf,.docx,.txt,.md,.jpg,.jpeg,.png,.webp"
        className="hidden"
        onChange={(e) => void addSource(e.target.files)}
      />
      <Button size="sm" variant="secondary" disabled={busy} onClick={() => fileInput.current?.click()}>
        {busy ? (
          <span className="flex items-center gap-1"><Spinner className="h-3 w-3" /> {t("Reading…")}</span>
        ) : (
          t("+ Add source")
        )}
      </Button>
      {error && <Alert>{error}</Alert>}
    </div>
  );

  if (surveys.length === 0) {
    return (
      <div className="space-y-3 p-3">
        {addBar}
        <p className="text-sm text-ink-faint">
          {t("The source index will appear here once the agent has read your files.")}
        </p>
      </div>
    );
  }
  return (
    <div className="h-full space-y-3 overflow-auto p-3">
      {addBar}
      {surveys.map((s) => (
        <details key={s.id} className="card" open>
          <summary className="cursor-pointer list-none text-sm font-semibold text-ink">
            {s.filename}
          </summary>
          {s.summary && (
            <p className="mt-1 text-xs text-ink-soft">
              <MathText text={s.summary} />
            </p>
          )}
          {s.structure.length > 0 && (
            <div className="mt-2">
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-brand-700">
                {t("Structure")}
              </p>
              <ul className="space-y-0.5 text-xs text-ink-soft">
                {s.structure.map((line, i) => (
                  <li key={i}>
                    <MathText text={line} />
                  </li>
                ))}
              </ul>
            </div>
          )}
          {s.keys.length > 0 && (
            <div className="mt-2">
              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-brand-700">
                {t("Keys")}
              </p>
              <ul className="list-disc space-y-0.5 pl-4 text-xs text-ink-soft">
                {s.keys.map((k, i) => (
                  <li key={i}>
                    <MathText text={k} />
                  </li>
                ))}
              </ul>
            </div>
          )}
        </details>
      ))}
    </div>
  );
}
